import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, FileText, LogIn, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import axios from 'axios';
import { ForgotPasswordLink, ForgotPasswordForm } from '../components/ForgotPassword';

const LOCKED_TXT = {
  it: 'Troppi tentativi errati. Per sicurezza l\'accesso è bloccato per {m} minuti. Se ha dimenticato la password usi "Password dimenticata?".',
  en: 'Too many failed attempts. For security, access is blocked for {m} minutes. If you forgot your password, use "Forgot password?".',
  de: 'Zu viele Fehlversuche. Aus Sicherheitsgründen ist der Zugang für {m} Minuten gesperrt. Falls Sie Ihr Passwort vergessen haben, nutzen Sie "Passwort vergessen?".',
  fr: 'Trop de tentatives échouées. Par sécurité, l\'accès est bloqué pendant {m} minutes. Si vous avez oublié votre mot de passe, utilisez « Mot de passe oublié ? ».',
};

const lockedMessage = (err, lang) => {
  const d = err.response?.status === 429 && err.response.data?.detail;
  return d?.code === 'locked' ? (LOCKED_TXT[lang] || LOCKED_TXT.it).replace('{m}', d.minutes) : null;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  it: {
    title: 'Area Riservata',
    subtitle: 'Accedi alla tua area personale',
    loginTab: 'Login',
    praticaTab: 'Accesso Pratica',
    email: 'Email',
    password: 'Password',
    numeroPratica: 'Numero Pratica',
    login: 'Accedi',
    accessPratica: 'Accedi alla Pratica',
    forgotPassword: 'Password dimenticata?',
    error: 'Credenziali non valide',
    praticaError: 'Numero pratica o email non corretti',
    praticaHelp: 'Inserisci il numero pratica ricevuto via email'
  },
  en: {
    title: 'Client Area',
    subtitle: 'Access your personal area',
    loginTab: 'Login',
    praticaTab: 'Case Access',
    email: 'Email',
    password: 'Password',
    numeroPratica: 'Case Number',
    login: 'Login',
    accessPratica: 'Access Case',
    forgotPassword: 'Forgot password?',
    error: 'Invalid credentials',
    praticaError: 'Invalid case number or email',
    praticaHelp: 'Enter the case number received by email'
  },
  de: {
    title: 'Kundenbereich',
    subtitle: 'Zugang zu Ihrem persönlichen Bereich',
    loginTab: 'Anmelden',
    praticaTab: 'Fallzugang',
    email: 'E-Mail',
    password: 'Passwort',
    numeroPratica: 'Fallnummer',
    login: 'Anmelden',
    accessPratica: 'Fall zugreifen',
    forgotPassword: 'Passwort vergessen?',
    error: 'Ungültige Anmeldedaten',
    praticaError: 'Ungültige Fallnummer oder E-Mail',
    praticaHelp: 'Geben Sie die per E-Mail erhaltene Fallnummer ein'
  },
  fr: {
    title: 'Espace Client',
    subtitle: 'Accédez à votre espace personnel',
    loginTab: 'Connexion',
    praticaTab: 'Accès Dossier',
    email: 'Email',
    password: 'Mot de passe',
    numeroPratica: 'Numéro de Dossier',
    login: 'Se connecter',
    accessPratica: 'Accéder au Dossier',
    forgotPassword: 'Mot de passe oublié?',
    error: 'Identifiants invalides',
    praticaError: 'Numéro de dossier ou email incorrect',
    praticaHelp: 'Entrez le numéro de dossier reçu par email'
  }
};

const LoginPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('login');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [praticaForm, setPraticaForm] = useState({ numero_pratica: '', email: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/area-clienti');
      }
    } catch (err) {
      setError(lockedMessage(err, language) || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handlePraticaAccess = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/access-pratica`, praticaForm);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/area-clienti');
    } catch (err) {
      setError(lockedMessage(err, language) || t.praticaError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FAF8F5] pt-28 pb-16">
      <SEO page="home" />
      
      <div className="max-w-md mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-[#C9A227]/40 mb-5">
              <Lock className="w-7 h-7 text-[#C9A227] stroke-[1.5]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#283880] tracking-tight mb-2">
              {t.title}
            </h1>
            <p className="text-[#5A6578] text-sm">{t.subtitle}</p>
          </div>

          {/* Card */}
          <div className="bg-white border border-[rgba(30,36,51,0.08)] shadow-[0_4px_24px_rgba(40,56,128,0.06)] overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-[rgba(30,36,51,0.08)]">
              <button
                onClick={() => { setActiveTab('login'); setError(''); }}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                  activeTab === 'login'
                    ? 'text-[#283880] border-b-2 border-[#C9A227] bg-[#FAF8F5]'
                    : 'text-[#5A6578] hover:text-[#283880]'
                }`}
              >
                <LogIn className="w-4 h-4 inline mr-2 stroke-[1.5]" />
                {t.loginTab}
              </button>
              <button
                onClick={() => { setActiveTab('pratica'); setError(''); }}
                className={`flex-1 py-4 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                  activeTab === 'pratica'
                    ? 'text-[#283880] border-b-2 border-[#C9A227] bg-[#FAF8F5]'
                    : 'text-[#5A6578] hover:text-[#283880]'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-2 stroke-[1.5]" />
                {t.praticaTab}
              </button>
            </div>

            {/* Form Content */}
            <div className="p-6">
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {activeTab === 'login' && forgotOpen ? (
                <ForgotPasswordForm lang={language} initialEmail={loginForm.email} onBack={() => setForgotOpen(false)} />
              ) : activeTab === 'login' ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.email}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="email"
                        value={loginForm.email}
                        data-testid="login-email-input"
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.password}
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={loginForm.password}
                        data-testid="login-password-input"
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                        data-testid="login-password-toggle"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="login-submit-btn"
                    className="w-full bg-[#283880] hover:bg-[#1e2d66] text-white py-3"
                  >
                    {loading ? '...' : t.login}
                  </Button>
                  <ForgotPasswordLink lang={language} onOpen={() => { setError(''); setForgotOpen(true); }} />
                </form>
              ) : (
                <form onSubmit={handlePraticaAccess} className="space-y-4">
                  <p className="text-sm text-slate-500 mb-4">{t.praticaHelp}</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.numeroPratica}
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        value={praticaForm.numero_pratica}
                        onChange={(e) => setPraticaForm({ ...praticaForm, numero_pratica: e.target.value.toUpperCase() })}
                        className="pl-10 uppercase"
                        placeholder="PRT-XXXXXXXX-XXXXXX"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t.email}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="email"
                        value={praticaForm.email}
                        onChange={(e) => setPraticaForm({ ...praticaForm, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#C9A227] hover:bg-[#b8911f] text-white py-3"
                  >
                    {loading ? '...' : t.accessPratica}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
